import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

class AgentManagementService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();

    return { idToken, token };
  }

  private extractDomain(emailOrUrl: string): string {
    if (emailOrUrl.includes("@")) {
      return emailOrUrl.split("@")[1].toLowerCase().trim();
    }

    let domain = emailOrUrl.toLowerCase().trim();

    domain = domain.replace(/^https?:\/\//, "");
    domain = domain.replace(/^www\./, "");
    domain = domain.replace(/\/$/, "");

    domain = domain.split("/")[0];

    return domain;
  }

  async getAllRegisteredAgents(): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Agent.list({
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error("Error fetching agents.");
        throw new Error("Failed to fetch agents");
      }

      return data;
    } catch (error) {
      console.error("Error fetching agents:", error);
      throw error;
    }
  }

  async getAgentById(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();
      const authMode = token ? "userPool" : "iam";

      const { data, errors } = await client.models.Agent.get(
        { agentId: id },
        {
          authMode: authMode as any,
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error fetching agent.");
        throw new Error("Failed to fetch agent");
      }

      return data;
    } catch (error) {
      console.error("Error fetching agent:", error);
      throw error;
    }
  }

  async createAgent(input: any): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      let domain: string;

      if (input.id) {
        domain = this.extractDomain(input.id);
      } else if (input.agentEmail && input.agentEmail.length > 0) {
        domain = this.extractDomain(input.agentEmail[0]);
      } else {
        throw new Error(
          "Agent must have either an ID or email to generate domain",
        );
      }

      const { data, errors } = await client.models.Agent.create(
        {
          ...input,
          agentId: domain,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error creating agent.");
        throw new Error("Failed to create agent");
      }

      return data;
    } catch (error) {
      console.error("Error creating agent:", error);
      throw error;
    }
  }

  async updateAgent(id: string, input: Partial<any>): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Agent.update(
        {
          agentId: id,
          ...input,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error updating agent.");
        throw new Error("Failed to update agent");
      }

      return data;
    } catch (error) {
      console.error("Error updating agent:", error);
      throw error;
    }
  }

  async deleteAgent(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data: agent } = await client.models.Agent.get(
        { agentId: id },
        { authMode: "userPool", authToken: token },
      );

      if (agent?.institutionId) {
        // List all and filter in JS
        const { data: allInstitutions } = await client.models.Institution.list({
          authMode: "userPool",
          authToken: token,
        });

        for (const institutionId of agent.institutionId) {
          if (!institutionId) continue;

          const institution = allInstitutions?.find(
            (inst) => inst.institutionId === institutionId,
          );

          if (!institution) continue;

          if (institution?.agentId) {
            const updatedAgentIds = institution.agentId.filter(
              (agentId) => agentId !== id && agentId !== null,
            );

            await client.models.Institution.update(
              {
                institutionId: institutionId,
                institutionName: institution.institutionName,
                agentId: updatedAgentIds,
              },
              { authMode: "userPool", authToken: token },
            );
          }
        }
      }

      const { data, errors } = await client.models.Agent.delete(
        { agentId: id },
        { authMode: "userPool", authToken: token },
      );

      if (errors) {
        console.error("Error deleting agent.");
        throw new Error("Failed to delete agent");
      }

      return data;
    } catch (error) {
      console.error("Error deleting agent:", error);
      throw error;
    }
  }
}

export const agentManagementService = new AgentManagementService();
